import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Heading, Flex, Tooltip, Tabs } from '@radix-ui/themes';
import HighchartsReact from 'highcharts-react-official';
import NoDataToDisplay from 'highcharts/modules/no-data-to-display';
import Exporting from 'highcharts/modules/exporting';
import ExportData from 'highcharts/modules/export-data';
import Highcharts from 'highcharts';
import {
  startOfYear,
  add,
  eachDayOfInterval,
  isThisHour,
  isToday,
  isThisWeek,
  isThisMonth,
  isThisQuarter,
  isThisYear,
  getTime,
  parseISO,
} from 'date-fns';
import { useSelector } from 'react-redux';

import SectionCard from '../../../../shared/SectionCard/SectionCard.jsx';
import DatePicker from '../../components/DatePicker.jsx';
import IntervalSelect from '../../components/IntervalSelect.jsx';
import { useGetChartData } from '../../components/useGetChartData.js';
import i18n from '../../../../app/i18n.js';
import styles from './DetailedChart.module.css';

// Initialize Highcharts modules (idempotent)
NoDataToDisplay(Highcharts);
Exporting(Highcharts);
ExportData(Highcharts);

// Register quarter date format
if (!Highcharts.dateFormats.Q) {
  Highcharts.dateFormats.Q = timestamp => {
    const date = add(new Date(timestamp), { days: 1 });
    const quarter = Math.floor((date.getMonth() + 3) / 3);
    return `Q${quarter} ${date.getFullYear()}`;
  };
}

// Register CSV export column fix (guard against double registration)
let csvColumnShiftRegistered = false;
if (!csvColumnShiftRegistered) {
  Highcharts.addEvent(Highcharts.Chart, 'exportData', e => {
    e.dataRows.forEach(el => el.shift());
  });
  csvColumnShiftRegistered = true;
}

// ─── Chart configuration per type ─────────────────────────────────────────────

const TAB_TYPES = ['cumulativeVisits', 'participation', 'totalVisits'];

const CHART_CONFIG = {
  cumulativeVisits: {
    color: '#3E63DD',
    accent: 'var(--indigo-9)',
    icon: 'fa-regular fa-chart-line',
    titleKey: 'components.cumulativeChart.title',
    subtitleKey: 'components.cumulativeChart.subtitle',
    csvKey: 'components.cumulativeChart.csvFileName',
    seriesNameKey: 'components.cumulativeChart.cumulativeVisits',
  },
  participation: {
    color: '#12A594',
    accent: 'var(--teal-9)',
    icon: 'fa-regular fa-users',
    titleKey: 'components.participationChart.title',
    subtitleKey: 'components.participationChart.subtitle',
    csvKey: 'components.chartWrapper.csvFileName',
    seriesNameKey: 'components.participationChart.peopleVisited',
  },
  totalVisits: {
    color: '#6E56CF',
    accent: 'var(--violet-9)',
    icon: 'fa-regular fa-chart-column',
    titleKey: 'components.totalVisitsChart.title',
    subtitleKey: 'components.totalVisitsChart.subtitle',
    csvKey: 'components.totalVisitsChart.csvFileName',
    seriesNameKey: 'components.totalVisitsChart.totalVisits',
  },
};

const DATE_FORMATS = {
  hour: '%I:%M %p',
  day: '%m/%d/%y',
  week: '%m/%d/%y',
  month: '%b %Y',
  quarter: '%Q',
  year: '%Y',
};

const INTERVAL_OPTIONS = [
  {
    label: i18n.t('reports:components.participationChart.filters.options.hour'),
    value: 'hour',
  },
  {
    label: i18n.t('reports:components.participationChart.filters.options.day'),
    value: 'day',
  },
  {
    label: i18n.t('reports:components.participationChart.filters.options.week'),
    value: 'week',
  },
  {
    label: i18n.t(
      'reports:components.participationChart.filters.options.month',
    ),
    value: 'month',
  },
  {
    label: i18n.t(
      'reports:components.participationChart.filters.options.quarter',
    ),
    value: 'quarter',
  },
  {
    label: i18n.t('reports:components.participationChart.filters.options.year'),
    value: 'year',
  },
];

const INITIAL_DATES = [startOfYear(new Date()), new Date()];

// ─── Data helpers ─────────────────────────────────────────────────────────────

const IS_CURRENT_PERIOD = {
  hour: isThisHour,
  day: isToday,
  week: isThisWeek,
  month: isThisMonth,
  quarter: isThisQuarter,
  year: isThisYear,
};

const createZones = (data, interval) => {
  if (data.length <= 1) return [];
  const dashStyle = IS_CURRENT_PERIOD[interval](data[data.length - 1][0])
    ? 'dash'
    : 'solid';
  return [{ value: data[data.length - 2][0] }, { dashStyle }];
};

const getFilteredOptions = ({ startDate, endDate }) => {
  const days = eachDayOfInterval({ start: startDate, end: endDate }).length;
  return days > 3
    ? INTERVAL_OPTIONS.filter(({ value }) => value !== 'hour')
    : INTERVAL_OPTIONS;
};

const getColumnTitles = (type, t, newAirbo) => {
  switch (type) {
    case 'cumulativeVisits':
      return {
        x: 'Date',
        y: t('components.cumulativeChart.cumulativeVisits'),
      };
    case 'totalVisits':
      return { x: 'Date', y: t('components.totalVisitsChart.totalVisits') };
    default:
      return {
        x: 'Date',
        y: t('components.participationChart.peopleVisited'),
        digest: newAirbo
          ? t('components.participationChart.postsPublished')
          : t('components.participationChart.tileDigest'),
      };
  }
};

const parseSeries = (rawData, interval, type, config) => {
  let rawSeries;
  let keys;

  if (type === 'participation') {
    rawSeries = rawData?.data?.attributes?.series?.data || [];
    keys = ['x', 'y', 'digest'];
  } else {
    rawSeries = rawData?.data?.attributes?.series?.[0]?.data || [];
    keys = ['x', 'y'];
  }

  const parsedData = rawSeries.map(([date, ...rest]) => [
    getTime(parseISO(date)),
    ...rest,
  ]);
  const zones = createZones(parsedData, interval);

  return [
    {
      type: 'areaspline',
      name: i18n.t(`reports:${config.seriesNameKey}`),
      data: parsedData,
      zoneAxis: 'x',
      zones,
      keys,
      color: config.color,
      fillColor: {
        linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
        stops: [
          [0, `${config.color}1F`],
          [1, `${config.color}03`],
        ],
      },
      lineWidth: 2.5,
      marker: {
        enabled: true,
        fillColor: '#FFFFFF',
        lineWidth: 2,
        lineColor: null,
        radius: 4,
        symbol: 'circle',
        states: {
          hover: { radius: 6, lineWidth: 2 },
        },
      },
    },
  ];
};

// ─── Highcharts options builder ───────────────────────────────────────────────

const buildOptions = (dateFormat, newAirbo, type, t, config) => ({
  title: { text: '' },
  legend: { enabled: false },
  credits: { enabled: false },
  chart: {
    spacing: [20, 10, 15, 10],
    backgroundColor: 'transparent',
    style: { fontFamily: 'inherit' },
  },
  plotOptions: {
    areaspline: {
      threshold: null,
    },
  },
  exporting: {
    enabled: false,
    filename: t(config.csvKey),
    csv: {
      dateFormat,
      columnHeaderFormatter(item, key) {
        if (!item || item instanceof Highcharts.Axis) {
          return item.options.title.text;
        }
        return { columnTitle: getColumnTitles(type, t, newAirbo)[key] };
      },
    },
  },
  yAxis: {
    title: { text: null },
    gridLineColor: '#E8E8EC',
    gridLineDashStyle: 'Dash',
    min: 0,
    labels: {
      style: { color: '#8B8D98', fontSize: '11px' },
    },
  },
  xAxis: {
    type: 'datetime',
    lineColor: '#E8E8EC',
    tickColor: '#E8E8EC',
    labels: {
      style: { color: '#8B8D98', fontSize: '11px' },
      formatter() {
        return Highcharts.dateFormat(dateFormat, this.value);
      },
    },
  },
  tooltip: {
    backgroundColor: '#1C2024',
    borderColor: '#1C2024',
    borderRadius: 10,
    shadow: false,
    style: {
      color: '#FFFFFF',
      fontSize: '13px',
    },
    positioner(labelWidth, labelHeight, point) {
      const chart = this.chart;
      const plotLeft = chart.plotLeft;
      const plotTop = chart.plotTop;
      const plotWidth = chart.plotWidth;

      let x = point.plotX + plotLeft - labelWidth / 2;
      let y = point.plotY + plotTop - labelHeight - 20;

      if (x < plotLeft) x = plotLeft + 10;
      if (x + labelWidth > plotLeft + plotWidth)
        x = plotLeft + plotWidth - labelWidth - 10;
      if (y < plotTop) y = point.plotY + plotTop + 40;

      return { x, y };
    },
    formatter() {
      const yVal = `<span style="font-size:15px;font-weight:700">${this.point.y}</span>`;
      if (type === 'participation') {
        const digestVal = `<span style="font-size:15px;font-weight:700">${this
          .point.options.digest || 0}</span>`;
        const digestLabel = newAirbo ? 'Posts published' : 'Digests sent';
        return `${yVal} Visitors<br/>${digestVal} ${digestLabel}`;
      }
      return `${yVal} Visitors`;
    },
  },
  lang: {
    noData: i18n.t('reports:components.chartWrapper.noData'),
  },
  noData: {
    style: {
      color: '#8B8D98',
      fontSize: '14px',
      fontWeight: '500',
    },
  },
});

// ─── Inner chart panel (rendered per active tab) ──────────────────────────────

const ChartPanel = ({ type, campaignId, filters, onFiltersChange }) => {
  const { t } = useTranslation('reports');
  const { newAirbo } = useSelector(state => state.currentBoard);
  const chartRef = useRef();
  const config = CHART_CONFIG[type];

  const { data, isLoading } = useGetChartData({
    ...filters,
    campaignId,
    newAirbo,
    type: type !== 'participation' ? type : undefined,
  });

  const filteredOptions = getFilteredOptions(filters);

  const [chartOptions, setChartOptions] = useState(
    buildOptions(DATE_FORMATS[filters.filterBy], newAirbo, type, t, config),
  );

  useEffect(() => {
    const series = data
      ? parseSeries(data, filters.filterBy, type, config)
      : [];
    setChartOptions({
      ...buildOptions(
        DATE_FORMATS[filters.filterBy],
        newAirbo,
        type,
        t,
        config,
      ),
      series,
    });
  }, [filters.filterBy, data, newAirbo, t, type]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (chartRef.current?.chart) {
      if (isLoading) {
        chartRef.current.chart.showLoading();
      } else {
        chartRef.current.chart.hideLoading();
      }
    }
  }, [isLoading]);

  const setSelectedDates = dates => {
    const datesObj = { startDate: dates[0], endDate: dates[1] };
    const options = getFilteredOptions(datesObj);
    const currentValid = options.find(
      ({ value }) => value === filters.filterBy,
    );
    onFiltersChange({
      filterBy: currentValid ? filters.filterBy : options[0].value,
      ...datesObj,
    });
  };

  const setFilterBy = filterBy => {
    onFiltersChange({ ...filters, filterBy });
  };

  const downloadCSV = () => {
    chartRef.current?.chart?.downloadCSV();
  };

  return (
    <>
      <p className={styles.subtitle}>{t(config.subtitleKey)}</p>

      <div className={styles.filterRow}>
        <span className={styles.filterLabel}>
          {t('components.chartWrapper.wantToSee')}
        </span>
        <DatePicker
          datePickerVariant="tertiary"
          initialDates={INITIAL_DATES}
          setSelectedDates={setSelectedDates}
        />
        <span className={styles.filterLabel}>
          {t('components.chartWrapper.by')}
        </span>
        <IntervalSelect
          options={filteredOptions}
          interval={filters.filterBy}
          setInterval={setFilterBy}
        />
        <div className={styles.filterSpacer} />
        <Tooltip content={t('components.chartWrapper.downloadCsv')}>
          <button
            className={styles.downloadButton}
            onClick={downloadCSV}
            type="button"
            aria-label={t('components.chartWrapper.downloadCsv')}
          >
            <i className="fa-regular fa-download" />
          </button>
        </Tooltip>
      </div>

      <div className={styles.chartContainer}>
        <HighchartsReact
          ref={chartRef}
          highcharts={Highcharts}
          options={chartOptions}
        />
      </div>
    </>
  );
};

// ─── Tabbed container ─────────────────────────────────────────────────────────

export const DetailedCharts = ({ campaignId }) => {
  const { t } = useTranslation('reports');
  const [activeTab, setActiveTab] = useState('cumulativeVisits');
  const [filters, setFilters] = useState({
    filterBy: 'week',
    startDate: INITIAL_DATES[0],
    endDate: INITIAL_DATES[1],
  });

  return (
    <SectionCard
      className={styles.overflowVisible}
      contentClassName={styles.overflowVisible}
      headerContent={
        <Flex align="center" gap="2">
          <i
            className="fa-regular fa-chart-line"
            style={{ color: 'var(--indigo-9)' }}
          />
          <Heading size="4" m="0">
            {t('summary.detailedCharts')}
          </Heading>
        </Flex>
      }
    >
      <div className={styles.tabsWrapper}>
        <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
          <Tabs.List size="2">
            {TAB_TYPES.map(type => (
              <Tabs.Trigger key={type} value={type}>
                <i
                  className={`${CHART_CONFIG[type].icon} ${styles.tabIcon}`}
                  style={{ color: CHART_CONFIG[type].accent }}
                />
                {t(CHART_CONFIG[type].titleKey)}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </Tabs.Root>
      </div>

      <ChartPanel
        type={activeTab}
        campaignId={campaignId}
        filters={filters}
        onFiltersChange={setFilters}
      />
    </SectionCard>
  );
};
